import React, { useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Divider } from 'primereact/divider';
import { Tooltip } from 'primereact/tooltip';
import DealerDropdown from '../../../components/common/DealerDropdown';
import ChassisDropdown from '../../../components/common/ChassisDropdown';
import { useLanguage } from '../../../contexts/LanguageContext';
import { validateDateRange } from '../../../utils/dateUtils';

const FullServiceInvoiceFilter = ({ onApplyFilters, onResetFilters }) => {
    const { th } = useLanguage();
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({
        dealerId: null,
        chassisNo: '',
        contractStartDate: null,
        contractEndDate: null
    });
    const [dateValidationErrors, setDateValidationErrors] = useState({});

    const handleFilterChange = (field, value) => {
        const updatedFilters = { ...filters, [field]: value };
        setFilters(updatedFilters);

        if (field === 'contractStartDate' || field === 'contractEndDate') {
            validateDateFields(field, value, updatedFilters);
        }
    };

    const validateDateFields = (changedField, changedValue, currentFilters) => {
        const { contractStartDate, contractEndDate } = currentFilters;

        if (contractStartDate && contractEndDate) {
            const validation = validateDateRange(contractStartDate, contractEndDate);
            const errorMessage = validation.isValid ? null : th('customerAddressBrand.dateValidationError');

            setDateValidationErrors({
                contractStartDate: errorMessage,
                contractEndDate: errorMessage
            });
        } else {
            setDateValidationErrors({
                contractStartDate: null,
                contractEndDate: null
            });
        }
    };

    const handleFilterToggle = () => {
        setFilterOpen((prev) => !prev);
    };

    const handleApplyFilters = () => {
        const hasDateError = Object.values(dateValidationErrors).some((error) => error !== null);
        if (hasDateError) {
            return;
        }
        const filterData = {
            dealerId: filters.dealerId?.id || 0,
            chassisNo: filters.chassisNo || '',
            contractStartDate: filters.contractStartDate || null,
            contractEndDate: filters.contractEndDate || null
        };

        if (onApplyFilters) {
            onApplyFilters(filterData);
        }
    };

    const handleResetFilters = () => {
        setFilters({
            dealerId: null,
            chassisNo: '',
            contractStartDate: null,
            contractEndDate: null
        });
        setDateValidationErrors({});

        if (onResetFilters) {
            onResetFilters();
        }
    };

    const handleDealerChange = (value) => {
        handleFilterChange('dealerId', value);
    };

    const handleChassisChange = (id) => {
        handleFilterChange('chassisNo', id || '');
    };


    return (
        <div className="generic-table-filter">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button icon="pi pi-filter" label={th('customerAddressBrand.advancedFilter')} onClick={handleFilterToggle} outlined={!filterOpen} className={filterOpen ? '' : ''} />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('customerAddressBrand.advancedFilters')}</div>

                    <div className="flex flex-wrap gap-2" style={{ alignItems: 'end' }}>
                        <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                            <div className="field mb-2 p-1">
                                <label htmlFor="dealerId" className="block mb-1 text-sm">
                                    {th('customerAddressBrand.dealer')}
                                </label>
                                <DealerDropdown value={filters.dealerId} onChange={handleDealerChange} className="w-full" placeholder={th('customerAddressBrand.dealerPlaceholder')} />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                            <div className="field mb-2 p-1">
                                <label htmlFor="chassisNo" className="block mb-1 text-sm">
                                    {th('fullServiceInvoice.filterFields.chassisNo') || 'Şasi No'}
                                </label>
                                <ChassisDropdown value={filters.chassisNo} onChange={handleChassisChange} className="w-full" disabled={false} />
                            </div>
                        </div>


                        <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                            <div className="field mb-2 p-1">
                                <label htmlFor="contractStartDate" className="block mb-1 text-sm">
                                    {th('customerAddressBrand.startDate')}
                                    {dateValidationErrors.contractStartDate && (
                                        <>
                                            <i id="start-date-error" className="pi pi-exclamation-triangle ml-2 text-red-500" style={{ fontSize: '0.75rem', cursor: 'help' }} />
                                            <Tooltip target="#start-date-error" content={dateValidationErrors.contractStartDate} position="top" />
                                        </>
                                    )}
                                </label>
                                <Calendar
                                    id="contractStartDate"
                                    value={filters.contractStartDate}
                                    onChange={(e) => handleFilterChange('contractStartDate', e.value)}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    placeholder={th('customerAddressBrand.startDatePlaceholder')}
                                    className={`w-full ${dateValidationErrors.contractStartDate ? 'p-invalid' : ''}`}
                                />
                            </div>
                        </div>

                        <div className="flex-auto" style={{ minWidth: '240px', maxWidth: '300px' }}>
                            <div className="field mb-2 p-1">
                                <label htmlFor="contractEndDate" className="block mb-1 text-sm">
                                    {th('customerAddressBrand.endDate')}
                                    {dateValidationErrors.contractEndDate && (
                                        <>
                                            <i id="end-date-error" className="pi pi-exclamation-triangle ml-2 text-red-500" style={{ fontSize: '0.75rem', cursor: 'help' }} />
                                            <Tooltip target="#end-date-error" content={dateValidationErrors.contractEndDate} position="top" />
                                        </>
                                    )}
                                </label>
                                <Calendar
                                    id="contractEndDate"
                                    value={filters.contractEndDate}
                                    onChange={(e) => handleFilterChange('contractEndDate', e.value)}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    placeholder={th('customerAddressBrand.endDatePlaceholder')}
                                    className={`w-full ${dateValidationErrors.contractEndDate ? 'p-invalid' : ''}`}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('customerAddressBrand.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleResetFilters} />
                        <Button label={th('customerAddressBrand.apply')} icon="pi pi-check" onClick={handleApplyFilters} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default FullServiceInvoiceFilter;
